import { useLocation, useNavigate } from "react-router";
import MainTable from "../../../components/tables/MainTable";
import endpoints from "../../../services/endpoints";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { setPreviousUrl } from "../../../store/slices/geolocationSlice";
import { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";

/**
 * Props interface for the ETopUpList component
 * @property {ReactNode} children - Optional children components to be rendered
 */
type Props = {
  children?: ReactNode;
};

/**
 * ETopUpList Component
 *
 * This component displays a table of E-Top Up requests with various features:
 * - Search functionality with rotating placeholders
 * - Sorting capabilities
 * - Action handling for viewing details
 * - Export functionality
 * - Filtering options
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The rendered E-Top Up list table
 */
export default function ETopUpList({ children }: Props) {
  // Navigation and state management hooks
  const navigation = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { etopUpreceived } = endpoints;
  const location = useLocation();
  const { translate } = useTranslation(adminContents);

  // State for managing search placeholder animation
  const [searchPlaceholder, setSearchPlaceholder] =
    useState<string>("transaction id");
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(window.devicePixelRatio || 1);

  /**
   * Table configuration object defining column properties
   * Each column can have:
   * - id: Unique identifier
   * - className: CSS classes for styling
   * - custom: Custom rendering functions
   * - sort: Sorting capability
   * - actionTypes: Available actions
   */
  const TableDetails: { [key: string]: TableDetailsItem } = {
    "Transaction ID": {
      id: "transaction_id",
      className: "min-w-[18px]",
    },
    "Requested Date": {
      id: "transaction_date",
      className: "min-w-[197px]",
      custom: ["transaction_date"],
      sort: true,
    },
    "User Code": {
      id: "requester_user_code",
      className: "min-w-[150px]",
    },
    "USSD Code": {
      id: "ussd_code",
      className: "min-w-[150px]",
    },
    "User Name": {
      id: "requester_name",
      sort: true,
    },
    Amount: {
      id: "amount",
      custom: ["advance_amount"],
    },
    "Transaction Type": {
      id: "transaction_type",
      className: "min-w-[230px]",
    },
    "Mode of Payment": {
      id: "mode_of_payment",
      className: "min-w-[80px]",
    },
    "Requester Location": {
      id: "requester_location",
    },
    Channel: {
      id: "channel",
      className: "min-w-[89px]",
    },
    Status: {
      id: "status",
      className: "min-w-[89px]",
      custom: ["custom_status"],
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]", // Adding className here
      actionTypes: ["view"],
    },
  };

  /**
   * Handles action events from the table
   * @param {string} id - The ID of the selected item
   * @param {string} key - The action key
   * @param {string} [text] - Optional text parameter
   */
  const handleActions = async (id: string, key: string, text?: string) => {
    dispatch(setPreviousUrl(location.pathname + location.search));
    navigation(`/e-top-up-request/e-top-up/${id}`);
  };

  // Static text content for empty state
  const staticText = {
    emptyScreenText: {
      heading1: translate("There are no advance payment added yet"),
    },
  };

  // Available search placeholder options
  const SEARCH_PLACEHOLDERS = [
    "transaction id",
    "requested user code",
    "requested name",
  ] as const;

  /**
   * Rotates the search placeholder text with animation
   * Uses a callback to maintain referential equality
   */
  const rotatePlaceholder = useCallback(() => {
    setIsTransitioning(true);

    setTimeout(() => {
      setSearchPlaceholder((prev) => {
        const currentIndex = SEARCH_PLACEHOLDERS.indexOf(
          prev as (typeof SEARCH_PLACEHOLDERS)[number]
        );
        const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length;
        return SEARCH_PLACEHOLDERS[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, []);

  // Set up interval for rotating search placeholder
  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  // Detect zoom level
  useEffect(() => {
    const handleResize = () => {
      setZoomLevel(window.devicePixelRatio || 1);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if zoom is greater than 110% (1.1)
  const isZoomedIn = zoomLevel > 1.1;

  const dynamicFilter = {
    "User Role": {
      mapItem: [
        "Admin",
        "Master Distributor",
        "Distributor",
        "Sub Distributor",
        "Agent",
        "Retailer",
        "Customer",
      ],
      type: "checkbox",
    },
    "Transaction Type": {
      mapItem: ["All", "Advance", "Paid"],
      type: "radio",
      default: "all",
    },
  };
    const dynamicFilterConfig: DynamicFilterConfig = {
      "Date Range": {
        label: "Date Range",
        type: "date",
        required: false,
        searchKey: {
          start: "start_date",
          end: "end_date",
        },
      },
      "User Role": {
        label: "User Role",
        type: "checkbox",
        options: [
          "Admin",
          "Master Distributor",
          "Distributor",
          "Sub Distributor",
          "Agent",
          "Retailer",
          "Customer",
        ],
        required: false,
        searchKey: "user_role",
      },
      "Transaction Type": {
        label: "Transaction Type",
        type: "radio",
        options: ["All", "Paid", "Advance"],
        required: false,
        default: "All",
        searchKey: "type",
        valueMapping: {
          All: "",
          Paid: "paid",
          Advance: "advance",
        }
      },
      "Status": {
        label: "Status",
        type: "checkbox",
        options: ["Approved", "Pending", "Rejected"],
        required: false,
        searchKey: "status",
      },
    };
  return (
    <div>
      <MainTable
        staticText={staticText}
        mainEndPoint={`${etopUpreceived}`}
        TableDetails={TableDetails}
        handleActions={handleActions}
        params={{ page_number: "1", sort: "transaction_date+desc" }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        placeholderClassName={`search-placeholder ${
          isTransitioning ? "transitioning" : ""
        }`}
        dynamicFilter={dynamicFilter}
        filters={true}
        statusNotFilter={true}
        exporting={true}
        stickyStyleTableHeader={"top-[0.5px]"}
        TabChildren={<div className="pt-6 pl-6">{children}</div>}
        dynamicFilterConfig={dynamicFilterConfig}
        filterHeight={isZoomedIn ? "h-EtopFilterZoomHeight" : "h-[170px]"}
      />
    </div>
  );
}
